import _ from "lodash";

export const getTenantBackendUrlAndTenant = (
  domain: string,
): { tenant: string; url: string } => {
  const tenants = JSON.parse(process.env.NEXT_PUBLIC_TENANTS || "");
  const normalizeHost = (value: string) =>
    value
      .replace(/^https?:\/\//, "")
      .split("/")[0]
      .split(":")[0]
      .toLowerCase();

  let normalizedDomain = normalizeHost(domain);

  // Some callers pass only `www`; recover full host in browser runtime.
  if (
    normalizedDomain === "www" &&
    typeof window !== "undefined" &&
    window.location?.hostname
  ) {
    normalizedDomain = normalizeHost(window.location.hostname);
  }

  const hostParts = normalizedDomain.split(".").filter(Boolean);
  const nonWwwParts = hostParts.filter((part) => part !== "www");
  const tenantsSorted = tenants
    .slice()
    .sort((a: string, b: string) => b.length - a.length);

  const subdomain =
    tenantsSorted.find((tenant: string) => nonWwwParts.includes(tenant)) ||
    tenantsSorted.find(
      (tenant: string) =>
        normalizedDomain.startsWith(`${tenant}.`) ||
        normalizedDomain.includes(`.${tenant}.`),
    ) ||
    tenantsSorted.find((tenant: string) => normalizedDomain.includes(tenant)) ||
    "";
  const tenantUrlMap: { [key: string]: string | undefined } = {
    kode: process.env.NEXT_PUBLIC_KODE_BASE_URL,
    ryze: process.env.NEXT_PUBLIC_RYZE_BASE_URL,
    owest: process.env.NEXT_PUBLIC_OWEST_BASE_URL,
    dev: process.env.NEXT_PUBLIC_DEV_BASE_URL,
    staging: process.env.NEXT_PUBLIC_STAGING_BASE_URL,
    "staging-v2": process.env.NEXT_PUBLIC_STAGING_V2_BASE_URL,
  };

  const tenantTenantMap: { [key: string]: string | undefined } = {
    kode: "KODE",
    ryze: "RYZE",
    dev: "RYZE",
    staging: "RYZE",
    "staging-v2": "RYZE",
    owest: "OWEST",
  };
  let backendUrl = tenantUrlMap[subdomain];
  const tenant = tenantTenantMap[subdomain] || "RYZE";
  if (!backendUrl) {
    console.warn(
      `No backend URL found for subdomain: ${subdomain}. Falling back to default.`,
    );
    backendUrl = process.env.NEXT_PUBLIC_BASE_URL || "";
  }
  // Sanitize URL: remove quotes, semicolons, and ensure proper format
  backendUrl = backendUrl.replace(/^"|"$/g, "");
  backendUrl = backendUrl.replace(/;$/g, "");
  backendUrl = backendUrl.replace(/^https:\/([^/])/, "https://$1");
  backendUrl = backendUrl.replace(/\/$/, "");
  return { url: backendUrl, tenant };
};

export function convertEmptyStringsToNull(obj: any, method?: string) {
  if (method === "POST") {
    return obj;
  }
  const data = _.transform(obj, (result: any, value, key) => {
    if (_.isPlainObject(value) || _.isArray(value)) {
      result[key] = convertEmptyStringsToNull(value);
    } else if (value === "") {
      result[key] = null;
    } else if (value === undefined || value === null) {
      delete result[key];
    } else {
      result[key] = value;
    }
  });
  return data;
}

export function appendToFormData(
  formData: any,
  obj: any,
  parentKey = "",
  method?: string,
) {
  _.forEach(obj, (value, key) => {
    const newKey = parentKey ? `${parentKey}[${key}]` : key;
    if (_.isPlainObject(value)) {
      appendToFormData(formData, value, newKey);
    } else if (_.isArray(value)) {
      value.forEach((item, index) => {
        if (_.isPlainObject(item) || _.isArray(item)) {
          appendToFormData(formData, item, `${newKey}[${index}]`);
        } else {
          if (method === "POST") {
            if (item !== "" || item !== null) {
              formData.append(`${newKey}[${index}]`, item);
            }
          } else {
            formData.append(`${newKey}[${index}]`, item === "" ? null : item);
          }
        }
      });
    } else {
      if (method === "POST") {
        if (value !== "") {
          formData.append(newKey, value === "" ? null : value);
        }
      } else {
        formData.append(newKey, value === "" ? null : value);
      }
    }
  });
}

export const getAvatarColor = (initials: string) => {
  const colors = [
    "bg-red-500",
    "bg-blue-500",
    "bg-green-500",
    "bg-yellow-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-teal-500",
    "bg-orange-500",
    "bg-cyan-500",
    "bg-emerald-500",
    "bg-rose-500",
    "bg-violet-500",
    "bg-amber-500",
    "bg-lime-500",
    "bg-sky-500",
  ];

  if (!initials) return "bg-gray-300";

  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = initials.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};
